import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Sparkles, Hand, ImageIcon, Send, MessageCircle, Trash2, User, MoreHorizontal } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import Brand from "../../components/Brand";

const REACTIONS = [
  { kind: "amen",   label: "Amen",   Icon: Hand,      color: "text-[#F39A1F]" },
  { kind: "heart",  label: "Love",   Icon: Heart,     color: "text-[#2E9F3A]" },
  { kind: "praise", label: "Praise", Icon: Sparkles,  color: "text-[#3B7A3B]" },
  { kind: "pray",   label: "Pray",   Icon: Hand,      color: "text-[#D7800A]" },
];

const CATEGORIES = [
  ["prayer",    "🙏 Prayer Request"],
  ["praise",    "★ Praise Report"],
  ["testimony", "✓ Testimony"],
  ["general",   "● General"],
];

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s/60)}m ago`;
  if (s < 86400) return `${Math.floor(s/3600)}h ago`;
  if (s < 86400*7) return `${Math.floor(s/86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

/* ============== POST COMPOSER ============== */
function Composer({ onPosted }) {
  const fileRef = useRef(null);
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("prayer");
  const [image, setImage] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const { user } = useAuth();

  const handleImage = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Image too large (8MB max)."); return; }
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" }});
      setImage(r.data.url);
    } catch { toast.error("Could not upload image."); }
    setUploading(false);
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!content.trim()) { toast.error("Type something to share."); return; }
    setBusy(true);
    try {
      await api.post("/community/posts", { content: content.trim(), category, image });
      toast.success("Posted! +5 reward points.");
      setContent(""); setCategory("prayer"); setImage("");
      onPosted?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not post.");
    }
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="rounded-2xl border-2 border-[#F39A1F]/40 bg-white p-5 shadow" data-testid="post-form">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-full overflow-hidden border-2 border-[#F39A1F] bg-white flex items-center justify-center shrink-0">
          {user?.profile_photo ? <img src={user.profile_photo} alt="" className="w-full h-full object-cover"/> : <User className="w-6 h-6 text-foreground/40"/>}
        </div>
        <div className="flex-1">
          <textarea
            value={content} onChange={e=>setContent(e.target.value)}
            data-testid="post-content"
            rows={3} maxLength={2000}
            placeholder={`What's on your heart, ${user?.full_name?.split(" ")[0] || "friend"}? Share a prayer, praise, or testimony…`}
            className="w-full border-2 border-foreground/15 focus:border-[#F39A1F] rounded-md px-3 py-2.5 text-sm outline-none transition"
          />
          {image && (
            <div className="relative mt-2 rounded-md overflow-hidden border-2 border-foreground/15">
              <img src={image} alt="" className="w-full max-h-64 object-cover"/>
              <button type="button" onClick={() => setImage("")} className="absolute top-2 right-2 bg-onyx/80 text-white px-2 py-1 rounded text-[10px] tracking-wider-cap font-bold uppercase hover:bg-[#2E9F3A]">Remove</button>
            </div>
          )}
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <select value={category} onChange={e=>setCategory(e.target.value)} data-testid="post-category" className="px-3 py-2 rounded-md border-2 border-foreground/15 bg-white text-xs outline-none">
              {CATEGORIES.map(([k,l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="post-add-image" className="px-3 py-2 rounded-md border-2 border-foreground/15 bg-white text-xs hover:border-[#F39A1F] inline-flex items-center gap-1.5 disabled:opacity-60 transition">
              <ImageIcon className="w-3.5 h-3.5"/>{uploading ? "Uploading…" : "Photo"}
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e=>handleImage(e.target.files?.[0])}/>
            <button type="submit" disabled={busy} data-testid="post-submit" className="ml-auto bg-[#2E9F3A] hover:bg-[#1F8A2A] disabled:opacity-60 text-white px-5 py-2 rounded-md text-xs tracking-wider-cap font-bold uppercase inline-flex items-center gap-1.5 shadow transition">
              <Send className="w-3.5 h-3.5"/>{busy ? "Posting…" : "Share"}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
}

/* ============== SINGLE POST ============== */
function Post({ post, onChange }) {
  const { user } = useAuth();
  const [reactions, setReactions] = useState(post.reactions || {});
  const [showComments, setShowComments] = useState(false);
  const [comment, setComment] = useState("");
  const [posting, setPosting] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const isMine = user && (post.user_id === user.id);
  const canDelete = isMine || user?.is_admin;

  const myReaction = REACTIONS.find(r => (reactions[r.kind] || []).includes(user?.id));
  const totalReactions = REACTIONS.reduce((a, r) => a + (reactions[r.kind]?.length || 0), 0);

  const react = async (kind) => {
    try {
      const r = await api.post(`/community/posts/${post.id}/react`, { kind });
      // sync local counts
      const next = { ...reactions };
      REACTIONS.forEach(R => { next[R.kind] = next[R.kind] || []; });
      // optimistic
      Object.keys(next).forEach(k => next[k] = (next[k] || []).filter(uid => uid !== user.id));
      if (r.data.added) (next[kind] = next[kind] || []).push(user.id);
      setReactions(next);
    } catch { toast.error("Could not react."); }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    setPosting(true);
    try {
      await api.post(`/community/posts/${post.id}/comments`, { body: comment.trim() });
      setComment("");
      onChange?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not comment."); }
    setPosting(false);
  };

  const remove = async () => {
    if (!window.confirm("Delete this post?")) return;
    try { await api.delete(`/community/posts/${post.id}`); toast.success("Deleted."); onChange?.(); }
    catch { toast.error("Could not delete."); }
  };

  return (
    <article className="rounded-2xl border-2 border-foreground/10 bg-white p-5 shadow" data-testid={`post-${post.id}`}>
      {/* Header */}
      <div className="flex items-start gap-3 mb-3">
        <Link to={post.author_username ? `/u/${post.author_username}` : "#"} target={post.author_username ? "_blank" : undefined} className="shrink-0">
          <div className="w-11 h-11 rounded-full overflow-hidden border-2 border-[#F39A1F]/50 bg-white flex items-center justify-center">
            {post.author_photo ? <img src={post.author_photo} alt="" className="w-full h-full object-cover"/> : <User className="w-6 h-6 text-foreground/40"/>}
          </div>
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <Link to={post.author_username ? `/u/${post.author_username}` : "#"} target={post.author_username ? "_blank" : undefined} className="font-bold text-red-wine hover:underline">{post.author}</Link>
            <span className="text-[10px] tracking-wider-cap font-bold text-foreground/60">{timeAgo(post.created_at)}</span>
          </div>
          {post.author_headline && <div className="text-[11px] text-[#D7800A] italic">{post.author_headline}</div>}
          <div className="text-[10px] tracking-wider-cap font-bold text-gold-deep mt-1">{(CATEGORIES.find(c => c[0] === post.category) || ["", post.category])[1]}</div>
        </div>
        {canDelete && (
          <div className="relative">
            <button onClick={() => setShowMore(s=>!s)} className="text-foreground/50 hover:text-red-wine p-1 rounded hover:bg-white" data-testid={`post-${post.id}-more`}><MoreHorizontal className="w-4 h-4"/></button>
            {showMore && (
              <div className="absolute right-0 mt-1 bg-white border border-foreground/15 rounded-md shadow-lg z-10">
                <button onClick={remove} className="w-full text-left px-3 py-2 text-xs text-[#2E9F3A] hover:bg-white inline-flex items-center gap-2 whitespace-nowrap" data-testid={`post-${post.id}-delete`}><Trash2 className="w-3 h-3"/>Delete post</button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Body */}
      <div className="text-foreground whitespace-pre-wrap">{post.content}</div>
      {post.image && <img src={post.image} alt="" className="mt-3 w-full rounded-md max-h-96 object-cover border border-foreground/10"/>}

      {/* Reaction summary */}
      {totalReactions > 0 && (
        <div className="flex items-center gap-2 mt-3 pb-2 border-b border-foreground/10">
          <div className="flex -space-x-1">
            {REACTIONS.filter(r => (reactions[r.kind] || []).length > 0).map(r => (
              <div key={r.kind} className="w-6 h-6 rounded-full bg-white border-2 border-white flex items-center justify-center"><r.Icon className={`w-3 h-3 ${r.color}`}/></div>
            ))}
          </div>
          <div className="text-[11px] text-foreground/85">{totalReactions} {totalReactions === 1 ? "reaction" : "reactions"}</div>
          <button onClick={() => setShowComments(s=>!s)} className="ml-auto text-[11px] text-foreground/85 hover:text-red-wine" data-testid={`post-${post.id}-toggle-comments`}>
            {(post.comments_count || 0) > 0 ? `${post.comments_count} comments` : "Add a comment"}
          </button>
        </div>
      )}

      {/* Reaction buttons */}
      <div className="grid grid-cols-4 gap-1 mt-2">
        {REACTIONS.map(r => {
          const active = myReaction?.kind === r.kind;
          return (
            <button
              key={r.kind}
              onClick={() => react(r.kind)}
              data-testid={`react-${r.kind}-${post.id}`}
              className={`py-2 rounded-md text-[11px] tracking-wider-cap font-bold uppercase inline-flex items-center justify-center gap-1.5 transition ${active ? "bg-white text-red-wine" : "text-foreground/70 hover:bg-white/60"}`}
            >
              <r.Icon className={`w-4 h-4 ${active ? r.color : ""}`}/>{r.label}
              {(reactions[r.kind] || []).length > 0 && <span className="text-foreground/85">{reactions[r.kind].length}</span>}
            </button>
          );
        })}
      </div>

      {/* Comments toggle */}
      <button onClick={() => setShowComments(s=>!s)} className="mt-2 text-[11px] tracking-wider-cap font-bold text-foreground/70 hover:text-red-wine inline-flex items-center gap-1.5" data-testid={`post-${post.id}-comments-btn`}>
        <MessageCircle className="w-4 h-4"/>{showComments ? "Hide" : "Show"} comments {(post.comments_count || 0) > 0 && `(${post.comments_count})`}
      </button>

      {/* Comments */}
      {showComments && (
        <div className="mt-3 space-y-2 border-t border-foreground/10 pt-3" data-testid={`post-${post.id}-comments`}>
          {(post.comments || []).map(c => (
            <div key={c.id} className="flex items-start gap-2.5">
              <Link to={c.author_username ? `/u/${c.author_username}` : "#"} target={c.author_username ? "_blank" : undefined} className="shrink-0">
                <div className="w-8 h-8 rounded-full overflow-hidden border border-[#F39A1F]/40 bg-white flex items-center justify-center">
                  {c.author_photo ? <img src={c.author_photo} alt="" className="w-full h-full object-cover"/> : <User className="w-4 h-4 text-foreground/40"/>}
                </div>
              </Link>
              <div className="flex-1 bg-white/60 rounded-2xl px-3 py-2">
                <div className="flex items-baseline gap-2"><Link to={c.author_username ? `/u/${c.author_username}` : "#"} className="font-bold text-red-wine text-xs hover:underline">{c.author}</Link><span className="text-[10px] text-foreground/60">{timeAgo(c.created_at)}</span></div>
                <div className="text-sm text-foreground mt-0.5 whitespace-pre-wrap">{c.body}</div>
              </div>
            </div>
          ))}
          <form onSubmit={submitComment} className="flex items-start gap-2.5 pt-1" data-testid={`post-${post.id}-comment-form`}>
            <div className="w-8 h-8 rounded-full overflow-hidden border border-[#F39A1F]/40 bg-white flex items-center justify-center shrink-0">
              {user?.profile_photo ? <img src={user.profile_photo} alt="" className="w-full h-full object-cover"/> : <User className="w-4 h-4 text-foreground/40"/>}
            </div>
            <div className="flex-1 flex gap-1.5">
              <input type="text" value={comment} onChange={e=>setComment(e.target.value)} maxLength={1000} placeholder="Write a kind word…" data-testid={`post-${post.id}-comment-input`} className="flex-1 bg-white/60 rounded-full border border-foreground/15 focus:border-[#F39A1F] px-3 py-2 text-sm outline-none transition"/>
              <button type="submit" disabled={posting || !comment.trim()} data-testid={`post-${post.id}-comment-send`} className="bg-[#2E9F3A] disabled:opacity-50 hover:bg-[#1F8A2A] text-white px-3 py-2 rounded-full text-xs uppercase tracking-wider-cap font-bold inline-flex items-center gap-1 transition">
                <Send className="w-3.5 h-3.5"/>
              </button>
            </div>
          </form>
        </div>
      )}
    </article>
  );
}

/* ============== MAIN TAB ============== */
export default function CommunityTab() {
  const [posts, setPosts] = useState([]);
  const [points, setPoints] = useState(0);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    try {
      const [p, r] = await Promise.all([api.get("/community/posts"), api.get("/rewards")]);
      setPosts(p.data.posts || []);
      setPoints(r.data.points || 0);
    } catch { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const visible = filter === "all" ? posts : posts.filter(p => p.category === filter);

  return (
    <div className="space-y-6 max-w-2xl mx-auto" data-testid="community-tab">
      {/* Header */}
      <section className="rounded-2xl bg-gradient-to-br from-[#0F172A] via-[#2A2521] to-[#0F172A] text-white p-6 shadow-2xl border-2 border-[#F39A1F]/40">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] tracking-wider-cap font-bold text-[#F39A1F]">★ <Brand/> COMMUNITY</div>
            <h2 className="font-serif-display text-3xl uppercase">Pray · Encourage · Rejoice</h2>
            <p className="text-white/80 text-sm mt-1">Share, react, comment — meet other Christians on the platform.</p>
          </div>
          <div className="text-center">
            <div className="text-3xl text-[#F39A1F]">{points}</div>
            <div className="text-[9px] tracking-wider-cap font-bold text-white/60">REWARD POINTS</div>
          </div>
        </div>
      </section>

      {/* Composer */}
      <Composer onPosted={load}/>

      {/* Category filter */}
      <div className="inline-flex flex-wrap gap-1 rounded-full bg-white p-1 border border-border">
        <button onClick={() => setFilter("all")} data-testid="community-filter-all" className={`px-4 py-1.5 rounded-full text-xs font-overline uppercase tracking-wider-cap transition ${filter==="all" ? "bg-red-wine text-white shadow" : "text-foreground"}`}>All</button>
        {CATEGORIES.map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`community-filter-${k}`} className={`px-4 py-1.5 rounded-full text-xs font-overline uppercase tracking-wider-cap transition ${filter===k ? "bg-red-wine text-white shadow" : "text-foreground"}`}>{l}</button>
        ))}
      </div>

      {/* Feed */}
      <div className="space-y-4" data-testid="community-feed">
        {visible.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-[#F39A1F]/40 bg-white/40 p-10 text-center">
            <div className="font-serif-display text-2xl text-red-wine">Be the first to share.</div>
            <div className="text-foreground text-sm mt-1">A prayer, a praise, a verse that's stirring you — anything goes.</div>
          </div>
        ) : visible.map(p => <Post key={p.id} post={p} onChange={load}/>)}
      </div>
    </div>
  );
}
